/* xPT Notification */
export interface XptNotification {
  notification_id: string;
  source: string; // producer id, e.g. "access_management"
  event: string; // producer event/verb, e.g. "submit" | "approve" | ...
  payload: string; // AWSJSON (JSON-serialized string)
  is_persistent: boolean;
  is_read: boolean;
}

/** Codegen'd subscription payload: every non-key field is nullable and is_read never arrives. */
interface PushedNotification {
  notification_id: string;
  source: string;
  event: string;
  payload?: string | null;
  is_persistent?: boolean | null;
  is_read?: boolean | null;
}

/** Normalize a pushed notification, coalescing nullable fields. A pushed item is unread. */
export const fromPushedNotification = (n: PushedNotification): XptNotification => ({
  notification_id: n.notification_id,
  source: n.source,
  event: n.event,
  payload: n.payload ?? '{}',
  is_persistent: n.is_persistent ?? false,
  is_read: n.is_read ?? false
});

/* Access-management notification payload */

export const ACCESS_MANAGEMENT_SOURCE = 'access_management';

export type AccessRequestKind = 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface AccessRequestBase {
  request_id: string;
  bg_id: number;
  business_group_name: string;
  requested_role: string;
  cost_centers: string[];
  requester_alias: string;
  requester_name: string;
  beneficiary_alias: string;
  beneficiary_name: string;
}

export type ResolutionAction = 'APPROVED' | 'REJECTED' | 'CANCELLED';

interface ApprovalOutcome {
  is_partial_approval?: boolean;
  requested_cost_centers?: string[];
  granted_cost_centers?: string[];
  /** True when the approver granted a different role than requested. */
  is_role_changed?: boolean;
  /** The role actually granted (present when the approver changed it). */
  granted_role?: string | null;
}

export interface SubmittedAccessRequest extends AccessRequestBase, ApprovalOutcome {
  kind: 'SUBMITTED';
  resolution_action?: ResolutionAction;
  resolved_by?: string;
  resolved_by_name?: string;
}

/** The requester/beneficiary's standalone outcome item (own type: APPROVED / REJECTED). */
export interface ResolvedAccessRequest extends AccessRequestBase, ApprovalOutcome {
  kind: 'APPROVED' | 'REJECTED' | 'CANCELLED';
  resolved_by?: string;
  resolved_by_name?: string;
}

export type AccessRequestNotification = SubmittedAccessRequest | ResolvedAccessRequest;

const KIND_BY_EVENT: Record<string, AccessRequestKind> = {
  submit: 'SUBMITTED',
  approve: 'APPROVED',
  reject: 'REJECTED',
  cancel: 'CANCELLED'
};

export const parseAccessRequestNotification = (notification: XptNotification): AccessRequestNotification | null => {
  if (notification.source !== ACCESS_MANAGEMENT_SOURCE) return null;
  const kind = KIND_BY_EVENT[notification.event];
  if (!kind) return null;
  try {
    const payload = JSON.parse(notification.payload ?? '');
    // A malformed payload (double-encoded string, non-object) falls back to the generic message
    // rather than rendering "undefined (undefined) requested undefined".
    if (typeof payload !== 'object' || payload === null || !payload.request_id) {
      console.error('parseAccessRequestNotification: unexpected payload shape', {
        notification_id: notification.notification_id,
        event: notification.event,
        payload: notification.payload
      });
      return null;
    }
    return { ...payload, kind } as AccessRequestNotification;
  } catch (e) {
    console.error('parseAccessRequestNotification: payload is not valid JSON', {
      notification_id: notification.notification_id,
      payload: notification.payload,
      error: e
    });
    return null;
  }
};

/**
 * Whether the notification still represents an action waiting on someone.
 *
 * The publisher owns this: resolving a request sets is_persistent = FALSE on the row while merging
 * the outcome into the payload, then republishes the updated row (see access_management.py in
 * ExpensePlanningTool-BackendLambda). So the flag alone is authoritative and the client does not
 * need to inspect the payload for an outcome.
 */
export const isActionOutstanding = (notification: XptNotification): boolean => notification.is_persistent;

/** Unread if never read, or if it still represents an outstanding action. Used by bell and panel. */
export const isUnread = (n: XptNotification): boolean => !n.is_read || isActionOutstanding(n);

/** Only resolved items may be dismissed; hiding an outstanding action would lose the task. */
export const isDismissible = (n: XptNotification): boolean => !isActionOutstanding(n);

/**
 * Puts items awaiting action first, so a paged list cannot bury a request needing review.
 *
 * Stable, so the server's newest-first order is preserved within each group. Does not mutate.
 */
export const prioritizeActionable = (items: XptNotification[]): XptNotification[] =>
  [...items].sort((a, b) => Number(isActionOutstanding(b)) - Number(isActionOutstanding(a)));
